import { useState } from 'react';
import { Flex, Segmented, Tag } from 'antd';
import { useLazyExportStockXlsxQuery, useStockReportQuery } from '@/entities/reports';
import type { StockReportRowAggregated, StockReportRowByBatch } from '@/entities/reports';
import { ReportPage } from '@/shared/ui';
import type { ResponsiveColumns } from '@/shared/ui';
import { useBreakpoint } from '@/shared/lib/hooks';
import { useStoreContext } from '@/shared/lib/store-context';
import { formatDate, todayInTz } from '@/shared/lib/date';
import { displaySigned, formatMoney } from '@/shared/lib/money';
import { PeriodFilter } from './PeriodFilter';
import { presetRange } from './periodPresets';

// Пока после переключения режима не пришли новые данные, строки ещё прежнего режима — без этих полей.
const profitCell = (v: string | undefined) => (v === undefined ? '' : displaySigned(formatMoney(v)));
const marginCell = (v: string | null | undefined) => (v === undefined ? '' : v === null ? '—' : `${displaySigned(v)}%`);

/** §13, §6.2: остатки — К/Р/всего/себестоимость остатка, переключатель «по партиям». В сводном режиме
 *  ещё приход, прибыль с продажи и маржа ЗА ПЕРИОД (кнопки «Сегодня … Месяц» и произвольный диапазон);
 *  остаток всегда на сегодня. */
export function StockReportPage() {
  const ctx = useStoreContext();
  const { isMobile } = useBreakpoint();
  const today = todayInTz(ctx.timezone);
  const defaultPeriod = presetRange('month', today);
  const [byBatch, setByBatch] = useState(false);
  const [from, setFrom] = useState(defaultPeriod.from);
  const [to, setTo] = useState(defaultPeriod.to);
  const periodChanged = from !== defaultPeriod.from || to !== defaultPeriod.to;
  // Период — только сводному режиму: прибыль по партии не определена, в «по партиям» его не передаём.
  const params = byBatch ? { byBatch } : { byBatch, from, to };
  const { data, isFetching } = useStockReportQuery(params, { pollingInterval: 30_000 });
  const [exportXlsx] = useLazyExportStockXlsxQuery();

  const aggregatedColumns: ResponsiveColumns<StockReportRowAggregated> = [
    { title: 'Товар', dataIndex: 'categoryName' },
    { title: 'Коробок', dataIndex: 'sealedBoxes', width: 80, align: 'right' },
    { title: 'Россыпь', dataIndex: 'loosePcs', width: 80, align: 'right' },
    { title: 'Всего, шт', dataIndex: 'qtyTotal', width: 90, align: 'right' },
    { title: 'Себестоимость прихода', dataIndex: 'receiptCost', width: 130, align: 'right', render: (v: string) => formatMoney(v) },
    { title: 'Себестоимость остатка', dataIndex: 'costValue', width: 130, align: 'right', render: (v: string) => formatMoney(v) },
    { title: 'Прибыль с продажи', dataIndex: 'salesProfit', width: 120, align: 'right', render: profitCell },
    { title: 'Маржа, %', dataIndex: 'marginPct', width: 80, align: 'right', render: marginCell },
    {
      title: '',
      width: 140,
      mobile: 'subtitle',
      render: (_v, r) => (
        <>
          {r.hasProvisional ? <Tag color="warning">предварительная</Tag> : null}
          {r.recalcPending ? <Tag color="processing">пересчитывается</Tag> : null}
        </>
      ),
    },
  ];

  const byBatchColumns: ResponsiveColumns<StockReportRowByBatch> = [
    { title: 'Товар', dataIndex: 'categoryName' },
    { title: 'Партия', dataIndex: 'source', width: 130 },
    { title: 'Дата прихода', dataIndex: 'receivedDate', width: 120, render: (v: string) => formatDate(v) },
    { title: 'Срок годности', dataIndex: 'expiryDate', width: 120, render: (v: string | null) => (v ? formatDate(v) : '—') },
    { title: 'Остаток, шт', dataIndex: 'qtyRemaining', width: 110, align: 'right' },
    { title: 'Себестоимость ед.', dataIndex: 'unitCost', width: 140, align: 'right' },
  ];

  const rows = data?.rows ?? [];
  // Итог по деньгам — только в сводном режиме: в режиме «по партиям» единственная сумма — цена за единицу.
  // Считает сервер: маржа итога — Σ прибыли / Σ выручки, а не среднее процентов по строкам.
  const t = data?.totals;
  const totals =
    !byBatch && rows.length > 0 && t
      ? {
          cells: {
            receiptCost: formatMoney(t.receiptCost),
            costValue: formatMoney(t.costValue),
            salesProfit: profitCell(t.salesProfit),
            marginPct: marginCell(t.marginPct),
          },
        }
      : undefined;

  return (
    <ReportPage
      title="Остатки"
      filters={
        <Flex vertical={isMobile} wrap={!isMobile} gap={isMobile ? 12 : 8} align={isMobile ? 'stretch' : 'center'}>
          <Segmented
            block={isMobile}
            value={byBatch ? 'batch' : 'category'}
            onChange={(v) => setByBatch(v === 'batch')}
            options={[
              { value: 'category', label: 'По категориям' },
              { value: 'batch', label: 'По партиям' },
            ]}
          />
          {byBatch ? null : (
            <PeriodFilter
              from={from}
              to={to}
              today={today}
              onChange={(range) => {
                setFrom(range.from);
                setTo(range.to);
              }}
            />
          )}
        </Flex>
      }
      columns={byBatch ? (byBatchColumns as ResponsiveColumns<StockReportRowAggregated | StockReportRowByBatch>) : (aggregatedColumns as ResponsiveColumns<StockReportRowAggregated | StockReportRowByBatch>)}
      activeFilters={(byBatch ? 1 : 0) + (!byBatch && periodChanged ? 1 : 0)}
      onResetFilters={() => {
        setByBatch(false);
        setFrom(defaultPeriod.from);
        setTo(defaultPeriod.to);
      }}
      filtersSummary={byBatch ? 'По партиям' : `По категориям · ${formatDate(from)} — ${formatDate(to)}`}
      data={rows}
      totals={totals}
      loading={isFetching}
      rowKey={(r) => ('batchId' in r ? r.batchId : r.categoryId)}
      onExport={() => exportXlsx(params).unwrap()}
      exportFilename="Остатки.xlsx"
    />
  );
}
